import { css, Rule } from 'glamor'
import * as PropTypes from 'prop-types'
import * as React from 'react'

import getStyles from './styles'

import { isElement } from '../../utils'
import { useTheme } from '../../theme'

type TableSize = 'small' | 'medium' | 'large'
const sizes: TableSize[] = ['small', 'medium', 'large']

interface ITableHeaderProps {
  children: React.ReactNode
  delimit?: boolean
  size?: 'small' | 'medium' | 'large'
}

export class TableHeader extends React.Component<ITableHeaderProps> {
  static propTypes = {
    children: PropTypes.node,
    delimit: PropTypes.bool,
    size: PropTypes.string,
  }

  render() {
    const { children, delimit, size, ...additional } = this.props
    return (
      <thead {...additional}>
        <TableRow delimit={delimit} size={size}>
          {React.Children.toArray(children).map(child =>
            isElement(child) ? React.cloneElement(child, { type: 'head' }) : child
          )}
        </TableRow>
      </thead>
    )
  }
}

interface ITableRowProps {
  children: React.ReactNode
  delimit?: boolean
  size?: TableSize
  onClick?: () => void
  last?: boolean
  style?: Rule
}

export const TableRow: React.FC<ITableRowProps> = ({
  children,
  delimit,
  last,
  size,
  style,
  onClick,
  ...additional
}) => {
  const styles = getStyles(useTheme())

  return (
    <tr {...css([styles.row.base, onClick && styles.row.clickable, style])} onClick={onClick} {...additional}>
      {React.Children.toArray(children).map(child =>
        isElement(child) ? React.cloneElement(child, { delimit, last, size }) : child
      )}
    </tr>
  )
}

TableRow.propTypes = {
  children: PropTypes.node,
  delimit: PropTypes.bool,
  size: PropTypes.oneOf(sizes),
  onClick: PropTypes.func,
  last: PropTypes.any,
  style: PropTypes.object,
}

interface ITableCellProps {
  children?: React.ReactNode
  delimit?: boolean
  size?: 'small' | 'medium' | 'large'
  onClick?: () => void
  last?: boolean
  style?: Rule
  type?: 'head' | 'cell'
  align?: 'left' | 'center' | 'right'
  colSpan?: number
  rowSpan?: number
  width?: number | string
  nowrap?: boolean
}

export const TableCell: React.FC<ITableCellProps> = ({
  children,
  delimit = true,
  size = 'medium',
  last,
  style,
  type = 'cell',
  align = 'left',
  width,
  nowrap,
  ...additional
}) => {
  const styles = getStyles(useTheme())

  const element = type === 'head' ? 'th' : type === 'cell' ? 'td' : null

  if (!element) {
    return null
  }

  return React.createElement(
    element,
    {
      ...css(
        styles.cell.base,
        type === 'head' && styles.cell.head,
        type !== 'head' && styles.cell.body,
        delimit && styles.cell.delimit,
        styles.cell.sizes[size],
        styles.cell.align[align],
        last && styles.cell.last,
        width != null && { width },
        nowrap && {
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
        },
        style
      ),
      ...additional,
    },
    children
  )
}

TableCell.propTypes = {
  children: PropTypes.node,
  delimit: PropTypes.bool,
  size: PropTypes.oneOf(sizes),
  onClick: PropTypes.func,
  last: PropTypes.any,
  style: PropTypes.object,
  type: PropTypes.oneOf(['head', 'cell']),
  align: PropTypes.oneOf(['left', 'center', 'right']),
  colSpan: PropTypes.number,
  rowSpan: PropTypes.number,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  nowrap: PropTypes.bool,
}

interface ITableProps {
  children: React.ReactNode
  delimit?: boolean
  size?: 'small' | 'medium' | 'large'
  tableLayout?: 'auto' | 'fixed'
  style?: Rule
}

export const Table: React.FC<ITableProps> = ({ children, delimit, size, tableLayout, style, ...additional }) => {
  const styles = getStyles(useTheme())
  const childrenArr = React.Children.toArray(children)

  const header = childrenArr.find(child => isElement(child) && child.type === TableHeader)
  const rows = childrenArr.filter(child => isElement(child) && child.type === TableRow)

  return (
    <table {...css(styles.table.base, tableLayout != null && { tableLayout }, style)} {...additional}>
      {isElement(header) ? React.cloneElement(header, { delimit, size }) : header}
      <tbody>
        {rows.map((row, index) =>
          isElement(row)
            ? React.cloneElement(row, {
                delimit,
                size,
                last: index === rows.length - 1,
              })
            : row
        )}
      </tbody>
    </table>
  )
}

Table.propTypes = {
  children: PropTypes.node,
  delimit: PropTypes.bool,
  size: PropTypes.oneOf(sizes),
  tableLayout: PropTypes.oneOf(['auto', 'fixed']),
  style: PropTypes.object,
}

Table.defaultProps = {
  delimit: true,
  size: 'medium',
  style: {},
}
